/**
 * ehr#437 part 7 (PROGRESS_NOTE_10-14-V81 copied-text check, 26 C / 27 A / 28 A / 37 A): text copied
 * forward from a past note is remembered sentence by sentence; a sentence still word for word in
 * the box is flagged with a question or two to make it about today, and named at sign-off (a
 * warning, never a block). Pure, so it's tested on its own.
 */

export interface CopiedRecord {
  /** The past note's date, YYYY-MM-DD. */
  from: string;
  sentences: string[];
}

/** Sentences, split at . ! ? or a line break (V81 splitSentences). */
export function splitSentences(t: string): string[] {
  return t
    .split(/(?<=[.!?])\s+|\n+/)
    .map((x) => x.trim())
    .filter((x) => x.length >= 3);
}

/** The copied sentences still in the box word for word, each with when it was copied. */
export function unchangedCopied(
  list: CopiedRecord[] | undefined,
  text: string,
): { t: string; from: string }[] {
  const out: { t: string; from: string }[] = [];
  for (const c of list ?? [])
    for (const t of c.sentences)
      if (text.includes(t) && !out.some((o) => o.t === t)) out.push({ t, from: c.from });
  return out;
}

const BOX_QS: Record<string, string> = {
  subjective: 'What did the client report since last session?',
  data: 'What did the client report since last session?',
  behavior: 'What did the client report since last session?',
  objective: 'What did you observe today?',
  assessment: 'What changed since last session, and why is this level of care still needed?',
  plan: 'What changes for next session?',
  response: 'How did the client respond today?',
  intervention: 'What did you do in session today?',
};
/** The box's own question (V81 BOX_QS). */
export const boxQuestion = (key: string) => BOX_QS[key] ?? 'What is different today?';

const NUM_WORD = '(?:\\d+(?:\\.\\d+)?|one|two|three|four|five|six|seven|eight|nine|ten|twice|once)';
const STOP = new Set([
  'since',
  'before',
  'after',
  'mostly',
  'and',
  'but',
  'with',
  'in',
  'on',
  'at',
  'to',
  'of',
  'for',
  'this',
  'that',
  'when',
]);
/**
 * Up to 2 questions about the copied facts (V81 factQuestions): "Still true today: three panic
 * episodes?" for a number or count, "Did this happen: …?" for a "Next session:" item.
 */
export function factQuestions(sentences: string[]): { q: string; t: string }[] {
  const qs: { q: string; t: string; fact?: string }[] = [];
  for (const t of sentences) {
    if (qs.length >= 2) break;
    const nx = /next session:\s*([^.?!]+)/i.exec(t);
    if (nx) {
      qs.push({ q: `Did this happen: ${nx[1]!.trim()}?`, t });
      continue;
    }
    const re = new RegExp(`(?<![\\w:/-])${NUM_WORD}\\b(?![:/-])((?:\\s+[a-z][a-z-]*){1,4})`, 'ig');
    let m: RegExpExecArray | null;
    while ((m = re.exec(t)) && qs.length < 2) {
      if (/\bno\s+$/i.test(t.slice(0, m.index)) && /^one\b/i.test(m[0])) continue;
      const keep: string[] = [];
      for (const w of m[1]!.trim().split(/\s+/)) {
        if (STOP.has(w.toLowerCase())) break;
        keep.push(w);
      }
      if (!keep.length) continue;
      const fact = `${m[0].trim().split(/\s+/)[0]} ${keep.join(' ')}`.replace(/[,;:]+$/, '');
      if (!qs.some((x) => x.fact === fact)) qs.push({ q: `Still true today: ${fact}?`, t, fact });
    }
  }
  return qs.slice(0, 2).map(({ q, t }) => ({ q, t }));
}

/** The questions a copied box shows: its own, then up to 2 about the facts; at most 3. */
export function copyQuestions(key: string, marks: { t: string }[]): { q: string; t: string }[] {
  if (!marks.length) return [];
  return [{ q: boxQuestion(key), t: marks[0]!.t }, ...factQuestions(marks.map((m) => m.t))].slice(
    0,
    3,
  );
}
